import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useBulkComplianceDownload,
  useComplianceArchive,
} from "@/api/queries/reports";
import { useCurrentUser } from "@/api/queries/me";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";

export function CompliancePage() {
  const { identity } = useAuth();
  if (!isRespaiAdmin(identity?.role)) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">Compliance archive</h1>
        </header>
        <p className="text-sm text-destructive">
          Compliance archive is restricted to RespAI platform administrators.
        </p>
      </div>
    );
  }
  return <CompliancePageInner />;
}

function CompliancePageInner() {
  const me = useCurrentUser();
  const tz = me.data?.department?.timezone;
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const archive = useComplianceArchive({
    from: from || undefined,
    to: to || undefined,
  });
  const bulk = useBulkComplianceDownload();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Compliance archive</h1>
        <p className="text-sm text-muted-foreground">
          Finalized incident reports: bulk download for a date range.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Bulk download</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              From
              <Input
                type="date"
                className="mt-1 w-44"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="text-sm">
              To
              <Input
                type="date"
                className="mt-1 w-44"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
            <Button
              disabled={!from || !to || bulk.isPending}
              onClick={() => bulk.mutate({ from, to })}
            >
              {bulk.isPending ? "Preparing…" : "Download ZIP"}
            </Button>
          </div>
          {bulk.isSuccess ? (
            <p className="text-xs text-muted-foreground">
              Archive downloaded for {from} – {to}.
            </p>
          ) : null}
          {bulk.isError ? (
            <p className="text-xs text-destructive">
              {bulk.error instanceof Error
                ? bulk.error.message
                : "Download failed."}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Archive</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {archive.isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
            </div>
          ) : archive.data && archive.data.items.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Incident</Th>
                  <Th>Dispatched</Th>
                  <Th>Cleared</Th>
                  <Th>Status</Th>
                  <Th>PDF</Th>
                </Tr>
              </Thead>
              <Tbody>
                {archive.data.items.map((it) => (
                  <Tr key={it.incident_id}>
                    <Td className="font-medium">
                      <a
                        className="text-primary hover:underline"
                        href={`/incidents/${it.incident_id}`}
                      >
                        {it.incident_number ?? it.incident_id.slice(0, 8)}
                      </a>
                    </Td>
                    <Td>{formatDateTime(it.dispatched_at, tz)}</Td>
                    <Td>{formatDateTime(it.cleared_at ?? null, tz)}</Td>
                    <Td>{it.status}</Td>
                    <Td>
                      {it.pdf_url ? (
                        <a
                          className="text-primary hover:underline"
                          href={it.pdf_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        "—"
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              No finalized reports in the selected window.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
